// ═══════════════════════════════════════════════════════════════════════════
// PUSH REMINDERS — enregistrement du service worker + abonnement Web Push.
//
// Rappels de post-its (2026-05). L'abonnement est demandé de façon
// CONTEXTUELLE : on n'appelle `ensurePushReminders()` qu'au moment où
// l'utilisateur arme une cloche sur un post-it, jamais au chargement de la
// page (pas de prompt de permission intempestif).
// ═══════════════════════════════════════════════════════════════════════════

declare global {
  interface Window {
    ensurePushReminders: typeof ensurePushReminders;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

let swRegistration: ServiceWorkerRegistration | null = null;

async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  if (swRegistration) return swRegistration;
  try {
    swRegistration = await navigator.serviceWorker.register('/sw.js');
    return swRegistration;
  } catch (e) {
    safeWarn('serviceWorker.register(/sw.js)', e);
    return null;
  }
}

// Garantit qu'un abonnement Web Push existe pour cet appareil et qu'il est
// enregistré côté serveur. Retourne true si l'appareil recevra les pushs.
// Best-effort : toute défaillance (non supporté, permission refusée, push
// non configuré côté serveur) renvoie false sans lever.
async function ensurePushReminders(): Promise<boolean> {
  if (!pushSupported()) {
    toast(t('pagePostits.reminder.pushUnsupported'), 'warning');
    return false;
  }

  let vapid: { enabled: boolean; public_key: string | null };
  try {
    vapid = await api.getVapidPublicKey();
  } catch (e) {
    safeWarn('api.getVapidPublicKey', e);
    return false;
  }
  if (!vapid.enabled || !vapid.public_key) {
    toast(t('pagePostits.reminder.pushNotConfigured'), 'warning');
    return false;
  }

  const reg = await registerServiceWorker();
  if (!reg) return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    toast(t('pagePostits.reminder.permissionDenied'), 'warning');
    return false;
  }

  try {
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
      });
    }
    const json = sub.toJSON();
    const keys = json.keys || {};
    if (!json.endpoint || !keys['p256dh'] || !keys['auth']) return false;
    await api.subscribePush({
      endpoint: json.endpoint,
      keys: { p256dh: keys['p256dh'], auth: keys['auth'] },
      user_agent: navigator.userAgent,
    });
    return true;
  } catch (e) {
    safeWarn('pushManager.subscribe', e);
    return false;
  }
}

window.ensurePushReminders = ensurePushReminders;

export {};
